import React, { JSX } from "react";
import { ButtonSize } from "../../../../components/Button";
import { Button } from "../../../../components/Button";
import { OPAAvatar } from "../../../../components/OPAAvatar";
import { ButtonVariant } from "../../../../config/colors";
import { useRouter } from "../../../../hooks/useRouter";
import { PaymentType, Transaction } from "../../../../models/Transactions";
import { useLocation, useNavigate } from "react-router-dom";
import { SendTransactionStateRecipient } from "../../../../store/slices/transactionSlice";
import { BulkTypes, getDisplayNameBulkPayment } from "../../../../models/BulkPayment";
import { ChatChannelType } from "../../../../models/ChatChannel";
import { formatAmount } from "../../../../utils/Utils";

const buttonSize = ButtonSize.mediumSmall;

interface ButtonsInfosTxSenderReceiverProps {
    transaction?: Transaction;
    recipient?: SendTransactionStateRecipient | null;
    showZapBackAndAgain?: boolean;
    clickable?: boolean;
    onClickBulkRecipient?: (transaction: Transaction) => void;
}

const ButtonsInfosTxSenderReceiver = ({transaction, recipient, showZapBackAndAgain = true, clickable = true, onClickBulkRecipient}: ButtonsInfosTxSenderReceiverProps) => {
    const {navigateToUserProfile, navigateFromChannelTypeAndId} = useRouter();
    const location = useLocation();
    const navigate = useNavigate();
    const recipientButtonVariant = clickable ? ButtonVariant.primary : ButtonVariant.transparentWithBorder;

    let isSend: boolean = true;
    let internalUserSender: any = null;
    let internalUserReceipient: any = null;
    let lnurlRecipient: any = null;
    let invoiceRecipient: any = null;
    let bulkPaymentRecipient: JSX.Element | string | null = null;
    let fromBulkRecipient: any = null;
    let sendBackRecipient: any | null = null;
    let sendAgainRecipient: any | null = null;
    let handlerBulkRecipientClick: () => void = () => {};

    if(transaction)
    {
        isSend = transaction?.amount < 0;

        if(transaction?.type === PaymentType.bulk) {
            if(transaction.bulk?.name) {
                if(transaction.bulk?.type === BulkTypes.distanceFromMyself) {
                    bulkPaymentRecipient = <span>
                        <span>{formatAmount(transaction.bulk?.nbToSend ?? 0)} people within {transaction.bulk?.name} </span>
                    </span>
                } else {
                    bulkPaymentRecipient = <span>
                        <span>{formatAmount(transaction.bulk?.nbToSend ?? 0)} people in {transaction.bulk?.name} </span>
                    </span>
                }
            }

            if(onClickBulkRecipient) {
                handlerBulkRecipientClick = () => {
                    onClickBulkRecipient(transaction);
                }
            }
        }
            
        else if(transaction?.sender && transaction.sender.internalUser)
            internalUserSender = transaction.sender.internalUser;
        else if(transaction?.recipient && transaction.recipient.internalUser && transaction?.isInternal !== false)
            internalUserReceipient = transaction.recipient.internalUser;
        else if(transaction?.recipient && transaction.recipient.lnurl)
            lnurlRecipient = transaction.recipient.lnurl;
        else if(transaction?.recipient && transaction.recipient.invoice)
            invoiceRecipient = transaction.recipient.invoice;

        if(transaction?.fromBulk)
            fromBulkRecipient = transaction.fromBulk ?? null;

        if(showZapBackAndAgain) {
            if(!isSend && transaction?.sender?.internalUser?.zapAddress)        
                sendBackRecipient = {
                    lnurl: '',
                    pleb: transaction?.sender?.internalUser
                }
            if (isSend) {
                if(transaction?.recipient?.internalUser?.zapAddress)        
                    sendAgainRecipient = {
                        lnurl: '',
                        pleb: transaction?.recipient?.internalUser
                    } 
                else if(transaction?.bulk)        
                    sendAgainRecipient = {
                        lnurl: '',
                        pleb: null,
                        bulkPayment: transaction?.bulk
                    }
            }
        }  
    }
    else if(recipient)
    {
        if(recipient.pleb)
        {
            if(recipient.pleb.shouldShowNameAndPicture())
                internalUserReceipient = recipient.pleb;
            else
                lnurlRecipient = recipient.pleb.zapAddress ?? '';
        }
        else if(recipient.lnurl)
            lnurlRecipient = recipient.lnurl;
        else if(recipient.invoice)
            invoiceRecipient = recipient.invoice;
        else if(recipient.bulkPayment)
            bulkPaymentRecipient = getDisplayNameBulkPayment(recipient.bulkPayment);
    }

    return (
        <div className="flex flex-col justify-center items-center">

        {
            isSend && lnurlRecipient && (
                <Button
                    size={buttonSize}
                    variant={recipientButtonVariant}
                    className="py-1.5 rounded-2xl min-h-[42px]"
                    type="button"
                    title={lnurlRecipient}
                >
                    {
                        lnurlRecipient.length > 60 ? `${lnurlRecipient.slice(0, 8)}...${lnurlRecipient.slice(-8)}` : lnurlRecipient
                    }
                </Button>
            )
        }

        {
            isSend && invoiceRecipient && (
                <Button
                    size={buttonSize}
                    variant={recipientButtonVariant}
                    className="py-1.5 rounded-2xl min-h-[42px]"
                    type="button"
                    title={invoiceRecipient}
                >
                    {invoiceRecipient.slice(0, 8)}...{invoiceRecipient.slice(-8)}
                </Button>
            )
        }

        {
            bulkPaymentRecipient && (
                <Button
                    size={buttonSize}
                    variant={recipientButtonVariant}
                    className="py-1.5 rounded-2xl min-h-[42px]"
                    type="button"
                    onClick={handlerBulkRecipientClick}
                >
                    <span>{bulkPaymentRecipient}</span>
                    {
                        transaction?.bulk?.imgUrl && (
                            <OPAAvatar
                                imageUrl={transaction?.bulk?.imgUrl}
                                size={30}
                                borderWidth={0}
                                borderColor="var(--brand-white)"
                            />
                        )
                    }
                    {
                        recipient?.bulkPayment?.imgUrl && (
                            <OPAAvatar
                                imageUrl={recipient?.bulkPayment?.imgUrl}
                                size={30}
                                borderWidth={0}
                                borderColor="var(--brand-white)"
                            />
                        )
                    }
                </Button>
            )
        }

        {
            isSend && internalUserReceipient && (
                <Button
                    size={buttonSize}
                    variant={recipientButtonVariant}
                    onClick={() => clickable && navigateToUserProfile(internalUserReceipient?.id ?? '', location.pathname)}
                    className="py-1.5 rounded-2xl min-h-[42px]"
                    type="button"
                >
                    {internalUserReceipient.name}
                    <OPAAvatar
                        imageUrl={internalUserReceipient.image}
                        size={30}
                        borderWidth={0}
                        borderColor="var(--brand-white)"
                    />
                </Button>
            )
        }

        {
            fromBulkRecipient !== null && (
                <Button
                    size={buttonSize}
                    variant={recipientButtonVariant}
                    className="py-1.5 rounded-2xl min-h-[42px] mb-3"
                    type="button"
                    onClick={() => {
                        if(clickable)
                        {
                            if(fromBulkRecipient.type === BulkTypes.group)
                                navigateFromChannelTypeAndId(ChatChannelType.Group, fromBulkRecipient.id);
                            else if(fromBulkRecipient.type === BulkTypes.event)
                                navigateFromChannelTypeAndId(ChatChannelType.Event, fromBulkRecipient.id);
                        }
                    }}
                >
                    {fromBulkRecipient.name}
                    {
                        fromBulkRecipient.imgUrl && (
                            <OPAAvatar
                                imageUrl={fromBulkRecipient.imgUrl}
                                size={30}
                                borderWidth={0}
                                borderColor="var(--brand-white)"
                            />
                        )
                    }
                </Button>
            )
        }

            {
                !isSend && internalUserSender && (
                    <Button
                        size={buttonSize}
                        variant={recipientButtonVariant}
                        onClick={() => clickable && navigateToUserProfile(internalUserSender?.id ?? '', location.pathname)}
                        className="py-1.5 rounded-2xl min-h-[42px]"
                        type="button"
                    >
                        {internalUserSender.name}
                        <OPAAvatar
                            imageUrl={internalUserSender.image}
                            size={30}
                            borderWidth={0}
                            borderColor="var(--brand-white)"
                        />
                    </Button>
                )
            }

            {sendBackRecipient && (
                <Button
                    size={buttonSize}
                    variant={ButtonVariant.transparent}
                    className="py-1.5 rounded-2xl min-h-[42px]"
                    type="button"
                    onClick={() => {
                        navigate('/home/wallet/send/recipient', {
                            state: {
                                recipient: sendBackRecipient
                            }
                        });
                    }}
                >
                    ⚡ Zap Back
                </Button>
            )}

            {sendAgainRecipient && (
                <Button
                    size={buttonSize}
                    variant={ButtonVariant.transparent}
                    className="py-1.5 rounded-2xl min-h-[42px]"
                    type="button"
                    onClick={() => {
                        navigate('/home/wallet/send/recipient', {
                            state: {
                                recipient: sendAgainRecipient
                            }
                        });
                    }}
                >
                    ⚡ Zap Again
                </Button>
            )}
        </div>
    )
}

export default ButtonsInfosTxSenderReceiver;